import React from 'react';
import { RewriteFrames } from '@sentry/integrations';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import Crash from 'app/components/Crash';
import { getAriaLabelPath, isHTMLElement } from 'app/utils/DOM';
import { isProduction } from './varia';
const logError = (err, context) => {
    const renderedError = err === null || err === void 0 ? void 0 : err.message;
    if (context) {
        // eslint-disable-next-line no-console
        console.error(`[Error]: ${context}\n ${renderedError}`);
    }
    else {
        // eslint-disable-next-line no-console
        console.error(renderedError);
    }
};
const logBreadcrumb = (data, message) => {
    console.log('breadcrumb', message, JSON.stringify(data, null, 2));
};
// -----------------------
// Default fallback component when sentry is not enabled
// -----------------------
class DefaultErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        Object.defineProperty(this, "onErrorCatch", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (error, type) => {
                logError(error, `onErrorCatch: ${type}`);
                this.setState({ hasError: true });
            }
        });
        this.state = { eventId: null, hasError: false };
    }
    static getDerivedStateFromError(error) {
        logError(error, 'getDerivedStateFromError');
        return { hasError: true };
    }
    componentDidMount() {
        window.addEventListener('error', evt => this.onErrorCatch(evt.error, evt.type));
        window.addEventListener('unhandledrejection', evt => this.onErrorCatch(evt.reason, evt.type));
    }
    componentDidCatch(error, errorInfo) {
        logError(error, 'componentDidCatch');
        // eslint-disable-next-line no-console
        console.error('Component stack:\n', errorInfo.componentStack);
        this.setState({ eventId: 'NaN' });
    }
    render() {
        if (this.state.hasError) {
            // render fallback UI
            return React.createElement(Crash, { eventId: this.state.eventId });
        }
        return this.props.children;
    }
}
// Sentry error boundry
class ReactSentryErrorBoundary extends DefaultErrorBoundary {
    static getDerivedStateFromError(error) {
        logError(error, 'getDerivedStateFromError');
        return { hasError: true };
    }
    componentDidCatch(error, info) {
        Sentry.withScope(scope => {
            if (info) {
                scope.setExtras(info);
            }
            const eventId = Sentry.captureException(error);
            this.setState({ eventId, hasError: true });
        });
    }
    render() {
        if (this.state.hasError) {
            // render fallback UI
            return React.createElement(Crash, { eventId: this.state.eventId });
        }
        // when there's not an error, render children untouched
        return this.props.children;
    }
}
const createErrorNotifier = () => {
    let ErrorBoundary = DefaultErrorBoundary;
    let errorNotifier = (error, context) => logError(error, context);
    let leaveBreadcrumb = (data, message) => logBreadcrumb(data, message);
    if (isProduction) {
        try {
            const sentryApiKey = Env.config.SENTRY_TOKEN;
            if (typeof sentryApiKey === 'string' && sentryApiKey.length) {
                ErrorBoundary = ReactSentryErrorBoundary;
                // Log to console & to sentry
                errorNotifier = (error, context) => {
                    Sentry.captureException(error);
                    logError(error, context);
                };
                // Log to sentry
                leaveBreadcrumb = (data, message) => {
                    Sentry.addBreadcrumb({
                        type: 'debug',
                        level: 'log',
                        message,
                        data,
                    });
                };
            }
        }
        catch (error) {
            logError(error, 'Failed to load Sentry');
        }
    }
    return { ErrorBoundary, errorNotifier, leaveBreadcrumb };
};
export const { errorNotifier, leaveBreadcrumb, ErrorBoundary } = createErrorNotifier();
export function installErrorNotifier() {
    if (!isProduction) {
        return;
    }
    const sentryApiKey = Env.config.SENTRY_TOKEN;
    if (typeof sentryApiKey === 'string' && sentryApiKey.length) {
        const sentryInProduction = Env.config.SENTRY_STAGE === 'production';
        Sentry.init({
            integrations: [
                new RewriteFrames(),
                new Integrations.BrowserTracing({
                    tracingOrigins: [/^\//],
                    idleTimeout: 2000,
                }),
            ],
            // eslint-disable-next-line arrow-body-style
            tracesSampler: () => {
                return sentryInProduction ? 0.1 : 1.0;
            },
            dsn: sentryApiKey,
            environment: Env.config.SENTRY_STAGE || 'production',
            maxBreadcrumbs: 30,
            debug: sentryInProduction === false,
            release: Env.config.RELEASE.version,
            normalizeDepth: 3,
            tunnel: '/api/tunnel',
            beforeBreadcrumb(breadcrumb, hint) {
                var _a, _b;
                const { category } = breadcrumb;
                if (category === 'console') {
                    return null;
                }
                if (category === 'fetch') {
                    if ((_a = breadcrumb.data) === null || _a === void 0 ? void 0 : _a.url.match(/^data:/)) {
                        breadcrumb.data.url = breadcrumb.data.url.substring(0, breadcrumb.data.url.indexOf(','));
                    }
                    return breadcrumb;
                }
                if (category === 'ui.click') {
                    const target = (_b = hint === null || hint === void 0 ? void 0 : hint.event) === null || _b === void 0 ? void 0 : _b.target;
                    if (isHTMLElement(target)) {
                        const label = getAriaLabelPath(target, false, [
                            '__next',
                        ]);
                        const log = getLogger('breadcrumbs', 'debug');
                        log(target);
                        log(label);
                        if (label) {
                            breadcrumb.message = label;
                        }
                    }
                    return breadcrumb;
                }
                return breadcrumb;
            },
        });
        const userId = window.user_id || undefined;
        const ipAddress = window.ip_address || undefined;
        Sentry.configureScope(scope => {
            const userData = {};
            if (typeof ipAddress !== 'undefined') {
                userData.ip_address = ipAddress;
            }
            if (typeof userId !== 'undefined') {
                userData.id = userId;
            }
            scope.setUser(userData);
        });
        Sentry.configureScope(scope => scope.setTransactionName('editor'));
    }
    else {
        console.debug('Bugtracking is disabled');
    }
}
